/**
 * @file 相手役の一言を読み上げる hook。
 *
 * @what: 文と速さを渡すと、音声を取ってその場で鳴らす。同じ文と速さは1回しか取りに行かない。
 * @why: 1往復のあいだに何度も聞き直すので、同じ音声を取り直さない。※なぜなら待ち時間が増え、
 *   呼び出しの回数も増えるためである。
 * @failure: 取れなかった、または鳴らせなかったときは `error` に1行を入れる。
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { speakLine, speechAudioUrl } from "../api/speech";

export type LineSpeechState = "idle" | "loading" | "playing";

function keyOf(text: string, slow: boolean): string {
  return `${slow ? "slow" : "normal"}:${text}`;
}

export function useLineSpeech(familyId: string, personId: string) {
  const [state, setState] = useState<LineSpeechState>("idle");
  const [error, setError] = useState<string | null>(null);
  const cacheRef = useRef(new Map<string, string>());
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // 画面を閉じるときは鳴っている音を止める。
  useEffect(
    () => () => {
      audioRef.current?.pause();
      audioRef.current = null;
    },
    [],
  );

  const play = useCallback(
    async (text: string, slow = false): Promise<void> => {
      const line = text.trim();
      if (!line) return;
      setError(null);
      audioRef.current?.pause();
      const key = keyOf(line, slow);
      let url = cacheRef.current.get(key);
      if (url === undefined) {
        setState("loading");
        try {
          const res = await speakLine(familyId, personId, { text: line, slow });
          url = speechAudioUrl(res);
          cacheRef.current.set(key, url);
        } catch (err) {
          console.error("[study] failed to read the line aloud", err);
          setError("読み上げられませんでした。時間を置いてもう一度お試しください。");
          setState("idle");
          return;
        }
      }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setState("idle");
      try {
        setState("playing");
        await audio.play();
      } catch (err) {
        // 端末が音を鳴らせない (音量・権限・自動再生の制限) ときは、その旨だけ知らせる。
        console.warn("[study] could not play the audio", err);
        setError("音を鳴らせませんでした。端末の音量をご確認ください。");
        setState("idle");
      }
    },
    [familyId, personId],
  );

  return { state, error, play };
}
